import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    interpolationAcknowledgementState,
    resolvePendingInterpolationAcknowledgement,
} from '~/state/atoms/interpolationAtoms';

/**
 * Blocking acknowledgement shown after import when one or more
 * imported series had missing slices that were filled by linear
 * interpolation. Surfaces a clinically-significant transformation
 * that the user must explicitly acknowledge before continuing.
 *
 * The dialog cannot be dismissed by ESC or backdrop click — the
 * Acknowledge button is the only way to close it.
 */
const InterpolatedSlicesDialog: React.FC = () => {
    const [state, setState] = useRecoilState(interpolationAcknowledgementState);

    const handleAcknowledge = () => {
        setState({ open: false, images: [] });
        resolvePendingInterpolationAcknowledgement();
    };

    return (
        <Dialog
            open={state.open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleAcknowledge();
            }}
            disableEscapeKeyDown
            aria-labelledby="interpolated-slices-dialog-title"
            aria-describedby="interpolated-slices-dialog-description"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id="interpolated-slices-dialog-title">
                <FormattedMessage id="interpolatedSlices.title" />
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id="interpolated-slices-dialog-description"
                    component="div"
                >
                    <FormattedMessage id="interpolatedSlices.description" />
                </DialogContentText>
                <List dense disablePadding sx={{ mt: 1 }}>
                    {state.images.map((info) => (
                        <ListItem key={info.seriesUid} disableGutters>
                            <ListItemText
                                primary={
                                    <SensitiveInfoTypography>
                                        {info.seriesDescription || (
                                            <FormattedMessage id="unknown.series" />
                                        )}
                                    </SensitiveInfoTypography>
                                }
                                secondary={
                                    <FormattedMessage
                                        id="interpolatedSlices.itemDetail"
                                        values={{
                                            count: info.interpolatedSliceCount,
                                        }}
                                    />
                                }
                            />
                        </ListItem>
                    ))}
                </List>
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <FormattedMessage id="interpolatedSlices.warning" />
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleAcknowledge}
                    color="warning"
                    variant="contained"
                    autoFocus
                    data-testid="interpolated-slices-acknowledge"
                >
                    <FormattedMessage id="interpolatedSlices.acknowledge" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InterpolatedSlicesDialog;
